"use client";

import { Collapsible as ArkCollapsible } from "@ark-ui/react/collapsible";
import { forwardRef, ReactNode } from "react";
import { cva, cx, type RecipeVariantProps } from "styled-system/css";

export interface BaseCollapsibleProps {
  /** Whether to render as the child element instead of the default element. */
  asChild?: boolean;
  /**
   * The children of the element.
   * When `asChild` is `true`, this component will be rendered as the child provided.
   */
  children?: ReactNode;
  /** Class name for applying additional styles to the element. */
  className?: string;
}

export interface CollapsibleProps extends BaseCollapsibleProps {
  /**
   * When `true`, the collapsible will be expanded.
   * Use this when you want to use the collapsible as a controlled component.
   */
  open?: boolean;
  /** A callback function that is invoked when the open state of the collapsible changes. */
  onOpenChange?: (open: boolean) => void;
  /**
   * The initial open state of the collapsible when it is first rendered.
   * Use this when you do not need to use the collapsible as a controlled component.
   */
  defaultOpen?: boolean;
  /** When `true`, `Collapsible.Trigger` will be disabled. */
  disabled?: boolean;
  /**
   * When `true`, the collapsible content will only be mounted when the content is visible
   * and will unmount when the content is hidden.
   *
   * This is useful as a performance optimization for content that is expensive to render.
   */
  mountOnlyWhenVisible?: boolean;
}

const Root = forwardRef<HTMLDivElement, CollapsibleProps>(
  ({ onOpenChange, mountOnlyWhenVisible, ...props }, ref) => {
    return (
      <ArkCollapsible.Root
        ref={ref}
        lazyMount={mountOnlyWhenVisible}
        unmountOnExit={mountOnlyWhenVisible}
        onOpenChange={({ open }) => onOpenChange?.(open)}
        {...props}
      />
    );
  },
);
Root.displayName = "Collapsible";

const Trigger = forwardRef<HTMLButtonElement, BaseCollapsibleProps>((props, ref) => {
  return <ArkCollapsible.Trigger ref={ref} {...props} />;
});
Trigger.displayName = "Collapsible.Trigger";

export type CollapsibleDirection = NonNullable<
  RecipeVariantProps<typeof contentStyles>
>["direction"];

export interface ContentProps extends BaseCollapsibleProps {
  /** The direction that the content will expand and collapse in. */
  direction?: CollapsibleDirection;
}

const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ className, direction = "vertical", ...props }, ref) => {
    return (
      <ArkCollapsible.Content
        ref={ref}
        className={cx(contentStyles({ direction }), className)}
        {...props}
      />
    );
  },
);
Content.displayName = "Collapsible.Content";

const contentStyles = cva({
  base: {
    overflow: "hidden",
  },
  variants: {
    direction: {
      vertical: {
        "&[data-state='open']": {
          animationStyle: "expand-height",
        },
        "&[data-state='closed']": {
          animationStyle: "collapse-height",
        },
      },
      horizontal: {
        "&[data-state='open']": {
          animationStyle: "expand-width",
        },
        "&[data-state='closed']": {
          animationStyle: "collapse-width",
        },
      },
    },
  },
  defaultVariants: {
    direction: "vertical",
  },
});

/** A compound component for toggling the visibility of content in a collapsible section. */
export const Collapsible = Object.assign(Root, {
  /**
   * A component that renders a button that toggles the visibility of the content
   * in the collapsible section.
   */
  Trigger,
  /**
   * A component that renders the collapsible section itself.
   * This component will expand and collapse vertically when `Collapsible.Trigger` is pressed.
   */
  Content,
});
